import { ContentExtractionType } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ClientContentRecommendationEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DRAFT_FOLLOW_CONCURRENCY, IFaqDraft } from './FaqDraftFollower';
import type { ISuggestedFaqsSectionProps } from './FaqPanel';
import { ExtractedQaPair, loadQaPairsByExtraction } from './qaPairs.client';
import {
  createFaqSectionOptimization,
  dismissContentRecommendation,
  IFaqOptimizationContext,
  loadSuggestedFaqs,
  loadSuggestedFaqTerms,
} from './suggestedFaqs.client';
import { IResolvedSuggestionTerm, SuggestedFaqDraftState, SuggestedFaqRowState, SuggestedFaqTermResolver } from './SuggestedFaqs';

/**
 * The FAQ tab's reading, adding and draft following, beside the screen that
 * renders it. The screen keeps the markup and wires these values into the
 * panel; everything the tab holds between renders lives here.
 */

/** One draft being followed, and the suggestion that asked for it. */
export interface IFaqDraftFollow {
  suggestionId: string;
  taskGroupId: string;
}

/** Where a followed draft now stands, and the suggestion whose row says so. */
export interface IFaqDraftStateChange {
  suggestionId: string;
  state: SuggestedFaqDraftState;
}

/** What the tab needs from the screen around it. */
export interface ISuggestedFaqsOptions {
  api?: ISyncCoreApi;
  /** The page whose FAQs these are. */
  item?: ClientContentItemEntity;
  /** That page's internal id, or undefined when it cannot be read: the pairs are fetched once per id. */
  itemId?: string;
  /** True while the FAQ tab is the open one. The reads are lazy and start on the first open. */
  open: boolean;
  /**
   * The page may now have an optimization draft — a FAQ was just added, or a
   * followed draft has been written — so the screen asks for it again.
   */
  onDraftProbe: () => void;
}

/** The FAQ tab's whole state, ready to render. */
export interface ISuggestedFaqs {
  /** The questions the page already answers. */
  pairs: ExtractedQaPair[];
  pairsLoading: boolean;
  pairsError: boolean;
  reloadPairs: () => Promise<void>;
  /** Which of the Suggested FAQs section's four states the list is in. */
  state: ISuggestedFaqsSectionProps['state'];
  suggestions: ClientContentRecommendationEntity[];
  rowStates: Record<string, SuggestedFaqRowState>;
  resolveTerm: SuggestedFaqTermResolver;
  draftStates: Record<string, SuggestedFaqDraftState>;
  /** True when the page carries more suggestions than this screen reads in one go. */
  capped: boolean;
  /**
   * True when this page's optimizations could not all be read, so a row that was
   * already added may still show its Add action.
   */
  addedStateIncomplete: boolean;
  reloadSuggestions: () => Promise<void>;
  add: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  dismiss: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  announcement?: string;
  announcementKey: number;
  announce: (message: string) => void;
  /**
   * The drafts followed right now. At most a few at a time, so adding ten FAQs
   * does not put ten reads on the wire every two seconds; the rest wait their
   * turn and their rows say what the followed ones say.
   */
  follows: IFaqDraftFollow[];
  onDraftState: (change: IFaqDraftStateChange) => void;
}

/**
 * The FAQs suggested for one page: the page's own Q&A pairs, the open
 * suggestions with their term names, adding one to the page, dismissing one,
 * and following the draft an added one asks for.
 */
export function useSuggestedFaqs(options: ISuggestedFaqsOptions): ISuggestedFaqs {
  const { api, item, itemId, open } = options;

  // Held in a ref so a fresh handler each render never re-creates the callbacks
  // below or re-runs the reads.
  const onDraftProbeRef = useRef(options.onDraftProbe);
  onDraftProbeRef.current = options.onDraftProbe;

  const [pairs, setPairs] = useState<ExtractedQaPair[]>([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsError, setPairsError] = useState(false);
  const fetchedItemIdRef = useRef<string | undefined>(undefined);
  const pairsFetchIdRef = useRef(0);

  const [suggestions, setSuggestions] = useState<ClientContentRecommendationEntity[]>([]);
  const [suggestionsItemId, setSuggestionsItemId] = useState<string | undefined>(undefined);
  const [rowStates, setRowStates] = useState<Record<string, SuggestedFaqRowState>>({});
  const [terms, setTerms] = useState<Record<string, IResolvedSuggestionTerm>>({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(false);
  // Held as an object so the same word announced twice still renders a new node.
  const [announcement, setAnnouncement] = useState<{ message: string; nonce: number } | undefined>(undefined);
  // Every draft this visit asked for, by the suggestion that asked for it, so
  // a second add never silences the first row.
  const [drafts, setDrafts] = useState<Record<string, IFaqDraft>>({});
  const [capped, setCapped] = useState(false);
  const [addedStateIncomplete, setAddedStateIncomplete] = useState(false);
  const optimizationContextRef = useRef<IFaqOptimizationContext | undefined>(undefined);
  const suggestionsFetchIdRef = useRef(0);

  const reloadPairs = useCallback(async () => {
    if (!api || !item) {
      return;
    }

    const fetchId = pairsFetchIdRef.current + 1;
    pairsFetchIdRef.current = fetchId;

    // A page the tab shows for its suggested FAQs alone carries no Q&A pairs
    // extraction, and asking for a file that was never produced would leave the
    // pairs half of the tab on an error a reader can never clear.
    if (!item.extractions?.[ContentExtractionType.QaPairs]) {
      setPairs([]);
      setPairsError(false);
      setPairsLoading(false);
      return;
    }

    setPairsLoading(true);
    setPairsError(false);

    try {
      const loaded = await loadQaPairsByExtraction(item);
      if (pairsFetchIdRef.current !== fetchId) {
        return;
      }

      setPairs(loaded);
      setPairsLoading(false);
    } catch {
      if (pairsFetchIdRef.current !== fetchId) {
        return;
      }

      setPairs([]);
      setPairsError(true);
      setPairsLoading(false);
    }
  }, [api, item]);

  const reloadSuggestions = useCallback(async () => {
    if (!api || !item?.key) {
      return;
    }

    const fetchId = suggestionsFetchIdRef.current + 1;
    suggestionsFetchIdRef.current = fetchId;
    setSuggestionsLoading(true);
    setSuggestionsError(false);

    try {
      const loaded = await loadSuggestedFaqs(api, item);
      if (suggestionsFetchIdRef.current !== fetchId) {
        return;
      }

      optimizationContextRef.current = loaded.optimizationContext;
      setSuggestionsItemId(`${item.id}`);
      setCapped(loaded.capped);
      setAddedStateIncomplete(loaded.addedStateIncomplete);
      setSuggestions(loaded.suggestions);
      setRowStates(loaded.states);
      setSuggestionsLoading(false);

      // The badges show a term's name, never its key, so an unresolved term
      // simply renders no badge until its name arrives.
      const resolved = await loadSuggestedFaqTerms(api, loaded.suggestions);
      if (suggestionsFetchIdRef.current !== fetchId) {
        return;
      }

      setTerms(resolved);
    } catch {
      if (suggestionsFetchIdRef.current !== fetchId) {
        return;
      }

      setSuggestions([]);
      setRowStates({});
      setSuggestionsError(true);
      setSuggestionsLoading(false);
    }
  }, [api, item]);

  const resolveTerm = useCallback<SuggestedFaqTermResolver>((termKey) => terms[termKey], [terms]);

  const draftStates = useMemo(() => {
    const states: Record<string, SuggestedFaqDraftState> = {};
    for (const [suggestionId, draft] of Object.entries(drafts)) {
      states[suggestionId] = draft.state;
    }

    return states;
  }, [drafts]);

  const follows = useMemo(
    () =>
      Object.entries(drafts)
        .filter(([, draft]) => !!draft.taskGroupId)
        .slice(0, DRAFT_FOLLOW_CONCURRENCY)
        .map(([suggestionId, draft]) => ({ suggestionId, taskGroupId: draft.taskGroupId as string })),
    [drafts]
  );

  const onDraftState = useCallback((change: IFaqDraftStateChange) => {
    setDrafts((held) => {
      if (!held[change.suggestionId]) {
        return held;
      }

      // The follow has ended, so the task group is dropped and the row keeps
      // only what it now says.
      return { ...held, [change.suggestionId]: { state: change.state } };
    });

    if (change.state === 'written') {
      // The draft exists, so the tab is asked for again and its comparison is
      // loaded once, by this event rather than by every add.
      onDraftProbeRef.current();
    }
  }, []);

  // The state is cleared when the page changes, but that runs after the commit
  // that first paints the new page, so the rows are gated on the page they were
  // loaded for as well.
  const suggestionsForThisPage = useMemo(
    () => (item?.id && suggestionsItemId === `${item.id}` ? suggestions : []),
    [item?.id, suggestions, suggestionsItemId]
  );

  const add = useCallback(
    async (suggestion: ClientContentRecommendationEntity) => {
      if (!api) {
        throw new Error('Unable to reach Sync Core.');
      }

      const created = await createFaqSectionOptimization(api, optimizationContextRef.current, suggestion);
      setRowStates((states) => ({ ...states, [suggestion.id]: 'added' }));
      setDrafts((held) => ({
        ...held,
        // Writing the draft is a task, and the create names the group that writes it. A create that
        // names no group leaves nothing to follow, so the row settles on what is true — the
        // optimization exists and its state cannot be read — rather than waiting on a follow that
        // never starts.
        [suggestion.id]: { taskGroupId: created.taskGroupId, state: created.taskGroupId ? 'generating' : 'unreadable' },
      }));
      // A followed group asks for the draft once it is written; with nothing to follow, the draft is
      // asked for at once, since no later event will.
      if (!created.taskGroupId) {
        onDraftProbeRef.current();
      }
    },
    [api]
  );

  const dismiss = useCallback(
    async (suggestion: ClientContentRecommendationEntity) => {
      if (!api) {
        throw new Error('Unable to reach Sync Core.');
      }

      await dismissContentRecommendation(api, suggestion.id);
      setSuggestions((current) => current.filter((candidate) => candidate.id !== suggestion.id));
    },
    [api]
  );

  const announce = useCallback((message: string) => {
    setAnnouncement((held) => ({ message, nonce: (held?.nonce ?? -1) + 1 }));
  }, []);

  // A new content item clears the held pairs and invalidates any fetch still in
  // progress, so the next selection of the FAQ tab loads the pairs afresh. This
  // runs before the lazy-fetch effect below, so the fetch that effect starts for
  // the new item keeps the current request id and its result is not discarded.
  useEffect(() => {
    setPairs([]);
    setPairsError(false);
    setPairsLoading(false);
    fetchedItemIdRef.current = undefined;
    pairsFetchIdRef.current += 1;

    setSuggestions([]);
    setSuggestionsItemId(undefined);
    setRowStates({});
    setTerms({});
    setSuggestionsError(false);
    setSuggestionsLoading(false);
    setAnnouncement(undefined);
    setDrafts({});
    setCapped(false);
    optimizationContextRef.current = undefined;
    suggestionsFetchIdRef.current += 1;
  }, [item?.id]);

  // The pairs file is fetched lazily, on the first selection of the FAQ tab, and
  // once per content item — a second selection reads the pairs already held.
  useEffect(() => {
    if (!open || !api || !item) {
      return;
    }

    if (!itemId || fetchedItemIdRef.current === itemId) {
      return;
    }

    fetchedItemIdRef.current = itemId;
    void reloadPairs();
    void reloadSuggestions();
  }, [api, item, itemId, open, reloadPairs, reloadSuggestions]);

  return {
    pairs,
    pairsLoading,
    pairsError,
    reloadPairs,
    state: suggestionsError ? 'error' : suggestionsLoading ? 'loading' : suggestionsForThisPage.length ? 'list' : 'empty',
    suggestions: suggestionsForThisPage,
    rowStates,
    resolveTerm,
    draftStates,
    capped,
    addedStateIncomplete,
    reloadSuggestions,
    add,
    dismiss,
    announcement: announcement?.message,
    announcementKey: announcement?.nonce ?? 0,
    announce,
    follows,
    onDraftState,
  };
}
